import { CheckCircleOutline, ErrorOutline, InfoOutlined } from '@mui/icons-material'
import type { AlertColor } from '@mui/material'
import React, { type ReactElement } from 'react'

import { NotificationSeverity } from '../../../../domain'

import { AlertTriangleOutlineIcon } from '../Branding'

/**
 * `NotificationSeverity` is a domain enum with its own string values (Step 1), MUI's `Alert`
 * expects its own `AlertColor` union. Both currently use the same four strings, but string enums
 * aren't structurally assignable to plain string-literal unions in TypeScript, so this maps
 * explicitly instead of casting.
 */
export const toAlertColor: Readonly<Record<NotificationSeverity, AlertColor>> = {
    [NotificationSeverity.ERROR]: 'error',
    [NotificationSeverity.INFO]: 'info',
    [NotificationSeverity.SUCCESS]: 'success',
    [NotificationSeverity.WARNING]: 'warning',
}

/**
 * MUI's default icons already match Figma's confirmed outline style for error/info/success.
 * `warning` has no dedicated Figma asset (confirmed via Figma - no `icon/warning/*` in the Icons
 * page, only `icon/alert/triangle/{outline,filled}` and `icon/alert/octagon`), so it reuses
 * `icon/alert/triangle/outline` - the same icon already used for warnings elsewhere in the app
 * (the deviations badge, `DeviationNotifications/Badge.tsx`) - via its existing `AlertTriangleOutlineIcon`
 * component rather than MUI's `WarningAmberOutlined`.
 * All four keys must be supplied: passing a partial `iconMapping` to MUI's `Alert` replaces its
 * default mapping entirely rather than merging into it.
 */
export const notificationIconMapping: Record<AlertColor, ReactElement> = {
    error: <ErrorOutline fontSize="inherit" />,
    info: <InfoOutlined fontSize="inherit" />,
    success: <CheckCircleOutline fontSize="inherit" />,
    warning: <AlertTriangleOutlineIcon fontSize="inherit" />,
}
